import { ReactNode } from "react";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatNumber } from "../../utils";
import { EMPTY_TEXT } from "./constants";
import { formatSignedAmount } from "./monthlyExpenseLayout";

const CARD_MIN_WIDTH = 200;

interface CloseSummaryCardsProps {
  monthTitle: string;
  estimateTitle: string;
  monthTotal: number;
  averageSales: number | null;
  averageManager: number | null;
  adjustedCount: number;
  monthlyImpact: number;
}

interface SummaryCardProps {
  title: string;
  isEstimate?: boolean;
  estimateTitle?: string;
  children: ReactNode;
}

function SummaryCard({ title, isEstimate = false, estimateTitle, children }: Readonly<SummaryCardProps>) {
  return (
    <Paper sx={{ flex: 1, minWidth: CARD_MIN_WIDTH }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {isEstimate && <Chip size="small" color="primary" variant="outlined" label={estimateTitle} sx={{ fontStyle: "italic" }} />}
      </Stack>
      {children}
    </Paper>
  );
}

const formatOrEmpty = (value: number | null) => (value === null ? EMPTY_TEXT : formatNumber(value));

/** ค่าใช้จ่ายรวม (actual) · Sales / Manager average (estimate) · the adjustments and their impact. */
export default function CloseSummaryCards({
  monthTitle,
  estimateTitle,
  monthTotal,
  averageSales,
  averageManager,
  adjustedCount,
  monthlyImpact,
}: Readonly<CloseSummaryCardsProps>) {
  return (
    <Stack direction="row" gap={1.5} flexWrap="wrap">
      <SummaryCard title={`ค่าใช้จ่ายรวมเดือน${monthTitle} (ยอดจริง)`}>
        <Typography variant="h6" fontWeight={600}>
          {formatNumber(monthTotal)}{" "}
          <Typography component="span" variant="body2" color="text.secondary">
            บาท
          </Typography>
        </Typography>
      </SummaryCard>
      <SummaryCard title="Sales เฉลี่ยต่อสายต่อวัน" isEstimate estimateTitle={estimateTitle}>
        <Typography variant="h6" fontWeight={600} fontStyle="italic" color="primary.dark">
          {formatOrEmpty(averageSales)}
        </Typography>
      </SummaryCard>
      <SummaryCard title="Manager เฉลี่ยต่อสายต่อวัน" isEstimate estimateTitle={estimateTitle}>
        <Typography variant="h6" fontWeight={600} fontStyle="italic" color="primary.dark">
          {formatOrEmpty(averageManager)}
        </Typography>
      </SummaryCard>
      <SummaryCard title="ปรับยอดประมาณการแล้ว">
        <Typography variant="h6" fontWeight={600}>
          {adjustedCount} ช่อง{" "}
          <Typography component="span" variant="body2" color={monthlyImpact < 0 ? "error.main" : "success.dark"}>
            {formatSignedAmount(monthlyImpact)} บาท/เดือน
          </Typography>
        </Typography>
      </SummaryCard>
    </Stack>
  );
}
